import { Container, Graphics, Text } from 'pixi.js';
import type { BoardTile as BoardTileData, BoardTileKind } from '../../model/board/BoardTypes';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface BoardTileWidgetOptions {
  size: number;
  /** Per-kind tint overrides. Themes pass these from their palette;
   * defaults match the original Good-time Casino brand. */
  tints?: Partial<Record<BoardTileKind, number>>;
  /** Border colour. Default brand pink. */
  accentColor?: number;
  /** Text colour. Default white. */
  textColor?: number;
}

/** Default colour per tile kind. cs-core's MMS uses similar
 * "warm = good, cool = neutral, dark = trigger" mapping. */
const DEFAULT_TINTS: Record<BoardTileKind, number> = {
  start: 0x6b3d77,
  cash: 0xff8a3d,
  clue: 0x42e2f5,
  location: 0xff6bd6,
  bonus: 0xfff59d,
  finish: 0xff3b6b,
};

/**
 * A single board tile rendered as a tinted rounded square with a label
 * above the kind glyph. Player token sits on top — tiles don't manage
 * the token themselves.
 */
export class BoardTileWidget extends Container {
  private readonly bg: Graphics;
  private readonly highlight: Graphics;

  constructor(public readonly data: BoardTileData, opts: BoardTileWidgetOptions) {
    super();

    const tints = { ...DEFAULT_TINTS, ...opts.tints };
    const tint = tints[data.kind];
    const accent = opts.accentColor ?? 0xff6bd6;
    const textColor = opts.textColor ?? 0xffffff;

    this.bg = new Graphics();
    this.bg
      .roundRect(-opts.size / 2, -opts.size / 2, opts.size, opts.size, opts.size * 0.14)
      .fill({ color: tint, alpha: 0.92 })
      .stroke({ color: 0xffffff, alpha: 0.18, width: 2 });
    this.addChild(this.bg);

    // Highlight ring drawn above the tile when "current". Hidden by default;
    // host calls setHighlighted(true) when the player is here.
    this.highlight = new Graphics();
    this.highlight.visible = false;
    this.highlight
      .roundRect(
        -opts.size / 2 - 4,
        -opts.size / 2 - 4,
        opts.size + 8,
        opts.size + 8,
        opts.size * 0.14 + 4,
      )
      .stroke({ color: accent, alpha: 0.95, width: 3 });
    this.addChild(this.highlight);

    const glyph = glyphForKind(data.kind);
    const glyphText = new Text({
      text: glyph,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.floor(opts.size * 0.42),
        align: 'center',
      },
    });
    glyphText.anchor.set(0.5, 0.5);
    glyphText.y = -opts.size * 0.08;
    this.addChild(glyphText);

    const label = new Text({
      text: data.label,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: Math.max(9, Math.floor(opts.size * 0.13)),
        fontWeight: '700',
        fill: textColor,
        align: 'center',
        letterSpacing: 0.5,
      },
    });
    label.anchor.set(0.5, 0);
    label.y = opts.size * 0.18;
    this.addChild(label);
  }

  setHighlighted(highlighted: boolean): void {
    this.highlight.visible = highlighted;
  }
}

function glyphForKind(kind: BoardTileKind): string {
  switch (kind) {
    case 'start':
      return '🏁';
    case 'cash':
      return '💰';
    case 'clue':
      return '🔍';
    case 'location':
      return '📍';
    case 'bonus':
      return '🎁';
    case 'finish':
      return '🏆';
  }
}
